/**
 * CHOOSE YOUR COLOR + the custom field — the X2 lane's view models
 * (`design/GT Crew.dc.html` / `design/GT Mobile Crew.dc.html` panel +
 * `ccShow` overlay; the shells' `charTiles` / `wornRows` / `ccCrew` blocks;
 * DESIGN-CHANGELOG 2026-08-18). Shared by both trees — geometry differs at
 * the screens (desktop tiles flex-fill, phone keeps the 44px swipe rail), the
 * model does not.
 *
 * Colors are NOT exclusive: `heldBy` exists so a tile can NAME who else is on
 * it (`SCARLET — ALSO DAVE`) and the PICKED row can show the crew, never to
 * block a pick. The one-per-player world was reversed the same day it was
 * drawn (X.md C2).
 */

import {
  fieldHex,
  fieldHL,
  fieldXY,
  hexToOk,
  inkOn,
  maxC,
  nearestPalHue,
  PAL,
} from '../lib/color.ts';
import { hueBg } from '../lib/format.ts';
import type { User } from '../lib/types.ts';
import type { QdStore } from './types.ts';

/** A member's oklch position: their custom color's true coordinates, or the
 * hue formula's fixed L/C. The shells' `okOfUser`. */
function okOfUser(u: User): { L: number; C: number; h: number } {
  if (u.col) {
    const ok = hexToOk(u.col);
    if (ok) return ok;
  }
  return { L: 0.62, C: 0.13, h: u.hue };
}

export interface CharTile {
  key: string;
  /** the palette hue — the screen calls `s.setMyHue(h)` with it */
  h: number;
  hueBg: string;
  ini: string;
  ink: string;
  isMine: boolean;
  bd: string;
  sh: string;
  tip: string;
}

export interface WornRow {
  key: string;
  ini: string;
  ink: string;
  bg: string;
  bd: string;
  tip: string;
}

export interface CcMarker {
  key: string;
  ini: string;
  nm: string;
  ink: string;
  bg: string;
  bd: string;
  tip: string;
  left: string;
  top: string;
}

export function characterVM(s: QdStore) {
  const me = s.me();
  const meIni = me ? (me.name[0] ?? '?').toUpperCase() : '?';

  /* Which member "claims" each palette entry, for naming only. Seeded hues are
     off the 18° grid, so a member claims the nearest entry within 9° (total on
     this grid — see color.test.ts); a member wearing a custom color holds no
     entry, so theirs frees up in the label. First claimant wins the mention. */
  const heldBy: Record<number, string> = {};
  for (const u of s.users) {
    if (u.col) continue;
    const k = nearestPalHue(u.hue);
    if (k !== null && heldBy[k] === undefined) heldBy[k] = u.k;
  }

  const charTiles: CharTile[] = PAL.map((p) => {
    const holder = heldBy[p.h];
    const mine = !!me && holder === me.k && !me.col;
    const hu = holder && !mine ? s.user(holder) : null;
    const bg = hueBg(p.h);
    return {
      key: 'h' + p.h,
      h: p.h,
      hueBg: bg,
      // Every tile previews YOUR initial at that hue — what you would look
      // like, not who is there. Who is there lives in the tip + PICKED row.
      ini: meIni,
      ink: inkOn(bg),
      isMine: mine,
      bd: mine ? 'var(--acc)' : 'var(--gt-ink)',
      sh: mine ? 'inset 0 0 0 2px var(--acc), inset 0 0 0 3px var(--gt-ink)' : 'none',
      tip: p.n + (mine ? ' — YOURS' : hu ? ' — ALSO ' + hu.name.toUpperCase() : ''),
    };
  });

  /* Who already wears what: avatar swatch + initial, full name in the tip. */
  const wornRows: WornRow[] = s.users.map((u) => {
    const self = !!me && u.k === me.k;
    const pal = u.col ? null : nearestPalHue(u.hue);
    const p = pal === null ? null : PAL.find((x) => x.h === pal);
    const bg = u.col || hueBg(u.hue);
    return {
      key: u.k,
      ini: (u.name[0] ?? '?').toUpperCase(),
      ink: inkOn(bg),
      bg,
      bd: self ? 'var(--acc)' : 'var(--gt-ink)',
      tip: u.name.toUpperCase() + ' · ' + (u.col ? 'CUSTOM' : p ? p.n : 'OFF-PALETTE') + (self ? ' · YOU' : ''),
    };
  });

  /* The crew, plotted on the field — exact, because both live in oklch. */
  const rows = s.users.map((u) => {
    const ok = okOfUser(u);
    const self = !!me && u.k === me.k;
    const clamped = ok.L < 0.34 - 0.01 || ok.L > 0.92 + 0.01;
    // The field rides the gamut edge, so a house-palette color sits at the
    // right hue and lightness but is quieter than the paint under it.
    const muted = ok.C < maxC(ok.L, ok.h) - 0.035;
    const { x, y } = fieldXY(ok);
    const bg = u.col || hueBg(u.hue);
    return {
      key: u.k,
      ini: (u.name[0] ?? '?').toUpperCase(),
      nm: u.name.toUpperCase(),
      ink: inkOn(bg),
      bg,
      bd: self ? 'var(--acc)' : 'var(--gt-ink)',
      tip:
        u.name.toUpperCase() +
        (self ? ' · YOU' : '') +
        ' · ' +
        Math.round(ok.h) +
        '°' +
        (muted ? ' · MUTED' : '') +
        (clamped ? ' · CLAMPED TO THE EDGE' : ''),
      x,
      y,
    };
  });

  /* Anything within one marker-width spiders around its shared centre, so no
     crewmate can hide behind another (`spread()` in the playground; the
     shells' inline copy). */
  const done: string[] = [];
  for (const m of rows) {
    const grp = rows.filter((o) => Math.hypot(o.x - m.x, o.y - m.y) < 0.075);
    if (grp.length < 2 || done.includes(grp[0]!.key)) continue;
    const cx = grp.reduce((a, o) => a + o.x, 0) / grp.length;
    const cy = grp.reduce((a, o) => a + o.y, 0) / grp.length;
    grp.forEach((o, i) => {
      const a = (i / grp.length) * Math.PI * 2 - Math.PI / 2;
      o.x = cx + Math.cos(a) * 0.05;
      o.y = cy + Math.sin(a) * 0.05;
      o.tip += ' · SHARES THIS SPOT';
      done.push(o.key);
    });
  }
  const ccMarkers: CcMarker[] = rows.map((m) => ({
    key: m.key,
    ini: m.ini,
    nm: m.nm,
    ink: m.ink,
    bg: m.bg,
    bd: m.bd,
    tip: m.tip,
    left: Math.min(99, Math.max(1, m.x * 100)) + '%',
    top: Math.min(99, Math.max(1, m.y * 100)) + '%',
  }));

  const sel = s.ccSel;
  const cur = sel ? fieldHL(s.ccU, s.ccV) : null;
  const curHex = sel ? fieldHex(s.ccU, s.ccV) : '';

  return {
    charTiles,
    wornRows,
    hasWorn: wornRows.length > 0,
    charHasAv: !!(me && me.av),
    // the CUSTOM tile
    ccIsMine: !!(me && me.col),
    ccBd: me && me.col ? 'var(--acc)' : 'var(--gt-ink)',
    ccSh: me && me.col ? 'inset 0 0 0 2px var(--acc), inset 0 0 0 3px var(--gt-ink)' : 'none',
    ccTip: me && me.col ? 'CUSTOM — YOURS (' + me.col.toUpperCase() + ')' : 'CUSTOM COLOR — PICK YOUR OWN',
    // the picker overlay
    ccShow: s.ccOpen,
    ccHasSel: sel,
    ccX: s.ccU * 100 + '%',
    ccY: s.ccV * 100 + '%',
    ccMarkers,
    ccCol: sel ? curHex : 'var(--gt-field)',
    ccColTxt: sel && cur ? curHex.toUpperCase() + ' · ' + Math.round(cur.h) + '° · L ' + cur.L.toFixed(2) + ' · C ' + cur.C.toFixed(2) : 'NOTHING PICKED YET',
    /** what `USE THIS COLOR ▸` commits — `''` disarms the button */
    ccCommitHex: sel ? curHex : '',
    ccBtnBd: sel ? 'var(--acc)' : 'var(--gt-line)',
    ccBtnClr: sel ? 'var(--acc)' : 'var(--gt-ghost)',
    ccBtnCur: sel ? 'pointer' : 'default',
  };
}
